import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import MainCard from "components/MainCard";
import { TailSpin } from "react-loader-spinner";
import { toast, ToastContainer } from "react-toastify";
import { FiSearch, FiSend } from "react-icons/fi";
import BackButton from "components/BackButton";
import { baseURLFinanceVendor } from "config/apiConfig";
import AutoWrapHeader from "utils/AutoWrapHeader";
import { customStyles } from "utils/customStyleDataTable";
import { fetchBatchInvoiceData } from "api/api";
import { formatDate, formatDateTimeFull } from "utils/date-fromate";
import axios from "axios";

const AssignBillingType = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredData, setFilteredData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedBillingTypes, setSelectedBillingTypes] = useState({});
    const [isAssigning, setIsAssigning] = useState(false);

    const billingTypeOptions = ["billing", "reimbursement"];

    const fetchPage = async () => {
        setIsLoading(true);
        try {
            const data = await fetchBatchInvoiceData();

            if (Array.isArray(data?.dataItems)) {
                const filtered = data.dataItems.filter(
                    (item) =>
                        item?.invoiceStatus === "Ready For Billing" ||
                        item?.invoiceStatus === "Re-Assigned"
                );
                setFilteredData(filtered);
            } else {
                setFilteredData([]);
            }
        } catch (err) {
            console.error("Error fetching batch invoice data:", err);
            setFilteredData([]);
            toast.error("Failed to fetch data");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPage();
    }, []);

    const handleBillingTypeChange = (e, row) => {
        const billingType = e.target.value;
        const invoiceNo = row?.invoiceNo;

        if (!invoiceNo) return;

        setSelectedBillingTypes((prev) => ({
            ...prev,
            [invoiceNo]: billingType,
        }));
    };

    const assignBillingType = async () => {
        const updateBillingTypePayload = Object.entries(selectedBillingTypes).map(
            ([invoiceNo, billingType]) => ({
                invoiceNo,
                billingType,
            })
        );

        if (updateBillingTypePayload) {
            console.log("updateBillingTypePayload", updateBillingTypePayload);
            return;
        }

        if (updateBillingTypePayload.length === 0) {
            toast.info("Please select at least one Billing Type.");
            return;
        }

        try {
            setIsAssigning(true);

            const token = localStorage.getItem("token");

            const response = await axios.post(
                `${baseURLFinanceVendor}bulkUpdateInvoiceBillingType`,
                updateBillingTypePayload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response?.data?.status) {
                toast.success(response?.data?.message || "Billing Type updated successfully");
                fetchPage();
                setSelectedBillingTypes({});
            }

        } catch (error) {
            console.error("Error updating Billing Type:", error);

            toast.error(
                error?.response?.data?.message ||
                "Error updating Billing Type"
            );
        } finally {
            setIsAssigning(false);
        }
    };

    const columns = [
        {
            name: <AutoWrapHeader text="Batch No" />,
            selector: (row) => row?.batchNo,
            sortable: true,
            width: "100px",
        },
        {
            name: <AutoWrapHeader text="Invoice No" />,
            selector: (row) => row?.invoiceNo,
            sortable: true,
            width: "180px",
        },
        {
            name: <AutoWrapHeader text="Vendor Name" />,
            selector: (row) => row?.vendorName,
            sortable: true,
            width: "300px",
        },
        {
            name: <AutoWrapHeader text="Closure Date" />,
            selector: (row) => row?.closureDate,
            sortable: true,
            grow: 1.1,
        },
        {
            name: <AutoWrapHeader text="AA Number" />,
            selector: (row) => row?.aaNo,
            sortable: true,
            grow: 1.1,
        },
        {
            name: <AutoWrapHeader text="IMEI Number" />,
            selector: (row) => row?.imeiNo,
            sortable: true,
            grow: 1.1,
        },
        {
            name: <AutoWrapHeader text="Assign Billing Type" />,
            cell: (row) => (
                <select
                    value={selectedBillingTypes?.[row?.invoiceNo] || ""}
                    onChange={(e) => handleBillingTypeChange(e, row)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full px-3 me-3 py-2 text-sm font-medium border border-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <option value="">Select Billing Type</option>
                    {billingTypeOptions.map((billingType, index) => (
                        <option key={index} value={billingType}>
                            {billingType
                                ?.toLowerCase()
                                .replace(/\b\w/g, (char) => char.toUpperCase())}
                        </option>
                    ))}
                </select>
            ),
            width: "250px",
        },
    ];

    const newFilteredData = React.useMemo(() => {
        if (!searchTerm.trim()) return filteredData;

        const lowerSearchTerm = searchTerm.toLowerCase();

        return filteredData.filter((item) => {
            return (
                (item?.batchNo || "").toString().toLowerCase().includes(lowerSearchTerm) ||
                (item?.vendorName || "").toLowerCase().includes(lowerSearchTerm) ||
                (item?.invoiceNo || "").toLowerCase().includes(lowerSearchTerm) ||
                (item?.closureDate || "").toLowerCase().includes(lowerSearchTerm) ||
                (item?.invoiceDate || "").toLowerCase().includes(lowerSearchTerm) ||
                (item?.aaNo || "").toLowerCase().includes(lowerSearchTerm) ||
                (item?.customerName || "").toLowerCase().includes(lowerSearchTerm) ||
                (item?.imeiNo || "").toLowerCase().includes(lowerSearchTerm) ||
                (item?.sellingPartner || "").toLowerCase().includes(lowerSearchTerm) ||
                (item?.brand || "").toLowerCase().includes(lowerSearchTerm) ||
                (item?.invoiceStatus || "").toLowerCase().includes(lowerSearchTerm)
            );
        });
    }, [searchTerm, filteredData]);

    return (
        <MainCard>
            <BackButton />
            <div>
                <ToastContainer />
                <div className="flex flex-wrap items-center gap-4 mb-3">
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                            <FiSearch />
                        </span>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}   // ✅ Live search
                            placeholder="Search by Batch No, Vendor, Invoice No, AA No, Closure Date, Invoice Date..."
                            className="pl-10 pr-4 py-2 w-72 md:w-[28rem] text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#6C5DD3]"
                        />
                    </div>
                </div>

                {/* Table Section */}
                <div className="overflow-auto rounded-[0.3rem] border border-gray-200 shadow-sm">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center h-[300px] gap-3">
                            <TailSpin
                                visible={true}
                                height="50"
                                width="50"
                                color="#7E22CE"
                                ariaLabel="tail-spin-loading"
                                radius="1"
                            />
                            <p className="text-sm font-medium text-gray-600">
                                Fetching claims for billing type assignment...
                            </p>
                        </div>
                    ) : newFilteredData.length > 0 ? (
                        <DataTable
                            columns={columns}
                            data={newFilteredData}
                            customStyles={customStyles}
                            striped
                            pagination
                            responsive
                            fixedHeader
                            fixedHeaderScrollHeight="68vh"
                            highlightOnHover
                            noDataComponent={
                                <div className="py-10 text-gray-500">
                                    No Data Available
                                </div>
                            }
                        />
                    ) : (
                        <div className="text-center mt-10 text-gray-500 font-medium">
                            No records found.
                        </div>
                    )}
                </div>

                {newFilteredData.length > 0 && (
                    <div className="flex justify-center mt-6">
                        <button
                            onClick={assignBillingType}
                            disabled={isAssigning}
                            className={`flex items-center justify-center gap-2 px-[1.5rem] py-[0.55rem] rounded-md text-sm font-semibold shadow-md transition-all duration-200 ${isAssigning
                                ? "bg-[#8a05e2] cursor-not-allowed opacity-80"
                                : "bg-[#8a05e2] hover:bg-[#6e04b5] text-white"
                                }`}
                        >
                            {isAssigning ? (
                                <>
                                    <TailSpin
                                        height="20"
                                        width="20"
                                        color="#ffffff"
                                        ariaLabel="assigning"
                                        visible
                                    />
                                    <span>Updating...</span>
                                </>
                            ) : (
                                <>
                                    <FiSend className="text-lg" />
                                    <span>Assign Billing Type</span>
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </MainCard>
    );
};

export default AssignBillingType;